import { createNavigation } from "next-intl/navigation"
import { defineRouting } from "next-intl/routing"

export type LocaleDirection = "ltr" | "rtl"
export type LocaleFontPreset = "latin" | "extended" | "rtl"

const localeFontFamilyByPreset: Record<LocaleFontPreset, string> = {
  latin: "Rethink Sans",
  extended: "Inter",
  rtl: "Noto Sans Hebrew",
}

// Locale metadata for UI (language switcher, etc.)
export const allLocales = [
  {
    locale: "bg",
    text: "Български",
    summary: "БГ",
    flag: "🇧🇬",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "bg-BG",
    fontPreset: "extended" as const,
  },
  {
    locale: "ru",
    text: "Русский",
    summary: "RU",
    flag: "🇷🇺",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "ru-RU",
    fontPreset: "extended" as const,
  },
  {
    locale: "he",
    text: "עברית",
    summary: "HE",
    flag: "🇮🇱",
    direction: "rtl" as const,
    isRtl: true,
    languageTag: "he-IL",
    fontPreset: "rtl" as const,
  },
  {
    locale: "en",
    text: "English",
    summary: "EN",
    flag: "🇬🇧",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "en-US",
    fontPreset: "latin" as const,
  },
  {
    locale: "el",
    text: "Ελληνικά",
    summary: "EL",
    flag: "🇬🇷",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "el-GR",
    fontPreset: "extended" as const,
  },
  {
    locale: "fr",
    text: "Français",
    summary: "FR",
    flag: "🇫🇷",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "fr-FR",
    fontPreset: "latin" as const,
  },
  {
    locale: "de",
    text: "Deutsch",
    summary: "DE",
    flag: "🇩🇪",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "de-DE",
    fontPreset: "latin" as const,
  },
  {
    locale: "it",
    text: "Italiano",
    summary: "IT",
    flag: "🇮🇹",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "it-IT",
    fontPreset: "latin" as const,
  },
  {
    locale: "es",
    text: "Español",
    summary: "ES",
    flag: "🇪🇸",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "es-ES",
    fontPreset: "latin" as const,
  },
  {
    locale: "sv",
    text: "Svenska",
    summary: "SV",
    flag: "🇸🇪",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "sv-SE",
    fontPreset: "latin" as const,
  },
  {
    locale: "da",
    text: "Dansk",
    summary: "DA",
    flag: "🇩🇰",
    direction: "ltr" as const,
    isRtl: false,
    languageTag: "da-DK",
    fontPreset: "latin" as const,
  }
] as const;

export type AppLocale = (typeof allLocales)[number]["locale"]
export type LocaleConfig = (typeof allLocales)[number]

export const localeMetadata = Object.fromEntries(
  allLocales.map((localeConfig) => [localeConfig.locale, localeConfig]),
) as Record<AppLocale, LocaleConfig>

export function getLocaleFontPreset(locale: string): LocaleFontPreset {
  const localeConfig =
    localeMetadata[locale as AppLocale] ??
    localeMetadata[routing.defaultLocale as AppLocale]

  return localeConfig.fontPreset
}

export function getLocaleFontFamily(locale: string): string {
  return localeFontFamilyByPreset[getLocaleFontPreset(locale)]
}

export const supportedLocales = allLocales.map(({ locale }) => locale) as [
  AppLocale,
  ...AppLocale[],
]

export const routing = defineRouting({
  // Derived from allLocales to avoid duplication
  locales: supportedLocales,

  // Used when no locale matches
  defaultLocale: "bg",

  // Always use locale prefix in URLs
  localePrefix: "always",
  
  // Disable automatic locale detection since we handle it in proxy
  localeDetection: false,
});

// Lightweight wrappers around Next.js' navigation APIs
// that will consider the routing configuration
export const {Link, redirect, usePathname, useRouter, getPathname} =
  createNavigation(routing);
